import { MessageCircle, type LucideIcon } from "lucide-react";
import { SiSlack, SiTelegram, SiWhatsapp, SiDiscord } from "react-icons/si";
import { BsMicrosoftTeams } from "react-icons/bs";
import type { IconType } from "react-icons";
import type { ComponentType, SVGProps } from "react";
import claudeCodeMark from "@/assets/runtime-marks/claude-code.svg";
import codexMark from "@/assets/runtime-marks/codex.svg";
import cursorMark from "@/assets/runtime-marks/cursor.svg";
import antigravityMark from "@/assets/runtime-marks/antigravity.png";
import grokMark from "@/assets/runtime-marks/grok.webp";
import openclawMark from "@/assets/runtime-marks/openclaw.svg";
import hermesMark from "@/assets/runtime-marks/hermes.png";
import lovableMark from "@/assets/runtime-marks/lovable.svg";
import manusMark from "@/assets/runtime-marks/manus.svg";
import perplexityMark from "@/assets/runtime-marks/perplexity.svg";
import replitMark from "@/assets/runtime-marks/replit.svg";
import Reveal from "@/components/Reveal";

type Icon =
  | LucideIcon
  | IconType
  | ComponentType<SVGProps<SVGSVGElement> & { className?: string; style?: React.CSSProperties }>;

const CHANNELS: Array<{ id: string; label: string; icon: Icon; color: string; comingSoon?: boolean }> = [
  { id: "slack",    label: "Slack",    icon: SiSlack,    color: "#611f69" },
  { id: "telegram", label: "Telegram", icon: SiTelegram, color: "#2aabee" },
  { id: "discord",  label: "Discord",  icon: SiDiscord,  color: "#5865f2", comingSoon: true },
  { id: "imessage", label: "iMessage", icon: MessageCircle, color: "#0a7cff", comingSoon: true },
  { id: "teams",    label: "Teams",    icon: BsMicrosoftTeams, color: "#4b53bc", comingSoon: true },
  { id: "whatsapp", label: "WhatsApp", icon: SiWhatsapp, color: "#25d366", comingSoon: true },
];

type Agent = { name: string; mark: string };

const ALL_AGENTS: Agent[] = [
  { name: "Claude Code", mark: claudeCodeMark },
  { name: "Codex",       mark: codexMark },
  { name: "Cursor",      mark: cursorMark },
  { name: "Antigravity", mark: antigravityMark },
  { name: "Grok",        mark: grokMark },
  { name: "OpenClaw",    mark: openclawMark },
  { name: "Hermes",      mark: hermesMark },
  { name: "Lovable",     mark: lovableMark },
  { name: "Manus",       mark: manusMark },
  { name: "Perplexity",  mark: perplexityMark },
  { name: "Replit",      mark: replitMark },
];

const VISIBLE_AGENTS = 5;

/**
 * Works where you are. Two condensed floating cards, side-by-side,
 * matched height. Each card splits left (eyebrow + headline) and
 * right (chat transcript / channel grid).
 */
const ChatAnyChannel = () => {
  return (
    <section
      aria-labelledby="chat-any-channel"
      className="relative pt-4 sm:pt-6 pb-16 sm:pb-24 bg-background"
    >
      <div className="max-w-content mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="mb-10 sm:mb-14 mx-auto max-w-3xl text-center">
            <h2 id="chat-any-channel" className="h-display max-w-[20ch] mx-auto">
              Works <em>exactly as you do.</em>
            </h2>
            <p className="mt-6 text-[17px] sm:text-[19px] leading-[1.4] text-muted-foreground mx-auto max-w-[52ch]">
              Every agent under one roof. Same brain, same memory, wherever the work happens.
            </p>
          </div>
        </Reveal>

        {(() => {
          const LeftCard = (
            <FloatCard floatDelay="0s">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-5 sm:gap-6 items-center h-full">
                <div>
                  <p className="eyebrow !mb-3 text-[11px]">15x productivity</p>
                  <h3 className="text-[22px] sm:text-[26px] font-bold leading-[1.05] tracking-[-0.02em] text-foreground">
                    Bring all your <em>agents</em> under one roof
                  </h3>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                  {ALL_AGENTS.slice(0, VISIBLE_AGENTS).map((a) => (
                  <div
                    key={a.name}
                    className="group relative h-11 w-11 sm:h-12 sm:w-12 rounded-[12px] bg-card flex items-center justify-center cursor-default hover:z-50"
                    style={{ boxShadow: "0 0 0 1px hsl(var(--foreground) / 0.08)" }}
                  >
                      <img
                        src={a.mark}
                        alt=""
                        className="h-5 w-5 sm:h-[22px] sm:w-[22px] [filter:brightness(0)_saturate(100%)] dark:[filter:brightness(0)_saturate(100%)_invert(1)]"
                        draggable={false}
                      />
                      <span className="pointer-events-none absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50">
                        <span className="whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-normal text-background shadow-lg">
                          {a.name}
                        </span>
                        <span className="absolute left-1/2 bottom-0 translate-y-1/2 -translate-x-1/2 block w-1.5 h-1.5 bg-foreground rotate-45" />
                      </span>
                    </div>
                  ))}
                  <div
                    className="group/more relative h-11 w-11 sm:h-12 sm:w-12 rounded-[12px] bg-card flex items-center justify-center cursor-default hover:z-50"
                    style={{ boxShadow: "0 0 0 1px hsl(var(--foreground) / 0.08)" }}
                  >
                    <span className="text-[10px] sm:text-[11px] font-normal text-muted-foreground leading-none">
                      +{ALL_AGENTS.length - VISIBLE_AGENTS}
                    </span>
                    <span className="pointer-events-none absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full opacity-0 group-hover/more:opacity-100 transition-opacity duration-150">
                      <span className="whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-normal text-background shadow-lg">
                        {ALL_AGENTS.slice(VISIBLE_AGENTS).map((a) => a.name).join(" · ")}
                      </span>
                      <span className="absolute left-1/2 bottom-0 translate-y-1/2 -translate-x-1/2 block w-1.5 h-1.5 bg-foreground rotate-45" />
                    </span>
                  </div>
                </div>
              </div>
            </FloatCard>
          );

          const RightCard = (
            <FloatCard floatDelay="-1.8s">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-5 sm:gap-6 items-center h-full">
                <div>
                  <p className="eyebrow !mb-3 text-[11px]">native messaging</p>
                  <h3 className="text-[22px] sm:text-[26px] font-bold leading-[1.05] tracking-[-0.02em] text-foreground">
                    Agents where you <em>already chat</em>
                  </h3>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                  {CHANNELS.map(({ id, label, icon: Icon, color, comingSoon }) => (
                    <div
                      key={id}
                      className="group relative h-11 w-11 sm:h-12 sm:w-12 rounded-[12px] bg-card flex items-center justify-center cursor-default"
                      style={{ boxShadow: "0 0 0 1px hsl(var(--foreground) / 0.08)" }}
                    >
                      <Icon className="h-5 w-5 sm:h-[22px] sm:w-[22px]" style={{ color }} aria-hidden />
                      <span className="pointer-events-none absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <span className="whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-normal text-background shadow-lg">
                          {label}{comingSoon && " – soon"}
                        </span>
                        <span className="absolute left-1/2 bottom-0 translate-y-1/2 -translate-x-1/2 block w-1.5 h-1.5 bg-foreground rotate-45" />
                      </span>
                      {comingSoon && (
                        <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </FloatCard>
          );

          return (
            <>
              {/* Desktop: side-by-side grid */}
              <div className="hidden md:grid md:grid-cols-2 gap-5 sm:gap-6 items-stretch">
                <Reveal>{LeftCard}</Reveal>
                <Reveal>{RightCard}</Reveal>
              </div>

              {/* Mobile: continuous horizontal marquee */}
              <div className="md:hidden mane-mobile-cards-marquee" aria-hidden={false}>
                <div className="mane-mobile-cards-marquee__track">
                  {[0, 1].map((dup) => (
                    <div key={dup} className="mane-mobile-cards-marquee__set">
                      <div className="mane-mobile-cards-marquee__item">{LeftCard}</div>
                      <div className="mane-mobile-cards-marquee__item">{RightCard}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          );
        })()}

      </div>
    </section>
  );
};

const FloatCard = ({
  children,
  floatDelay,
}: {
  children: React.ReactNode;
  floatDelay: string;
}) => (
  <div
    className="relative z-10 hover:z-50 focus-within:z-50 rounded-[20px] bg-card p-5 sm:p-6 h-full motion-safe:[animation:channel-float_6.5s_ease-in-out_var(--float-delay)_infinite]"
    style={{
      boxShadow: "0 0 0 1px hsl(var(--foreground) / 0.07), 0 6px 16px -12px hsl(var(--foreground) / 0.10)",
      ["--float-delay" as any]: floatDelay,
      ["--float-y" as any]: "-6px",
    }}
  >
    {children}
  </div>
);

export default ChatAnyChannel;
